/**
 * Checking reserve (docs/04 §7).
 * uncommitted is never presented as authoritative when the balance is stale (>7 days)
 * or missing.
 */

import { asCents, type Cents } from './money';
import { businessDate, diffDays, type BusinessDate } from './time';

/** Soft staleness threshold from docs/04 §7. */
export const CHECKING_BALANCE_STALE_DAYS = 7;

export type CashReservationReserveInput = {
  id: string;
  deleted_at: string | null;
  status: string;
  amount_reserved_cents: Cents;
};

export type ComputeCheckingReserveArgs = {
  asOf: BusinessDate;
  checking_balance_cents: Cents | null;
  checking_balance_updated_at: BusinessDate | null;
  reservations: readonly CashReservationReserveInput[];
};

export type CheckingReserve = {
  checking_balance_cents: Cents | null;
  checking_balance_updated_at: BusinessDate | null;
  /** Σ cash_reservations where status = 'reserved'. */
  committed_cents: Cents;
  /** checking_balance − committed; null when balance is unknown. */
  uncommitted_cents: Cents | null;
  /** True when updated_at is missing or older than 7 days before asOf. */
  balance_is_stale: boolean;
  /** False when stale or balance unknown — UI must not treat uncommitted as authoritative. */
  uncommitted_is_authoritative: boolean;
};

export function computeCheckingReserve(args: ComputeCheckingReserveArgs): CheckingReserve {
  let committed = 0n;
  for (const r of args.reservations) {
    if (r.deleted_at !== null) continue;
    if (r.status !== 'reserved') continue;
    committed += r.amount_reserved_cents;
  }
  const committedCents = asCents(committed);

  const updatedAt = args.checking_balance_updated_at;
  let balanceIsStale = updatedAt === null;
  if (updatedAt !== null) {
    const age = diffDays(businessDate(updatedAt), args.asOf);
    balanceIsStale = age > CHECKING_BALANCE_STALE_DAYS;
  }

  const balance = args.checking_balance_cents;
  const uncommitted = balance === null ? null : asCents(balance - committedCents);

  const authoritative = balance !== null && !balanceIsStale;

  return {
    checking_balance_cents: balance,
    checking_balance_updated_at: updatedAt,
    committed_cents: committedCents,
    uncommitted_cents: uncommitted,
    balance_is_stale: balanceIsStale,
    uncommitted_is_authoritative: authoritative,
  };
}
